import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { FACET_META } from "@/lib/explore";
import { KineticText, Reveal } from "@/components/motion/primitives";
import { ArrowRight, Loader2, Search } from "lucide-react";
import Seo from "@/components/Seo";

const GROUP_ORDER = ["god", "prophets", "kings", "disciples", "people"];
const GROUP_LABELS = { god: "God & Jesus", prophets: "Prophets", kings: "Kings, Rulers & Psalmists", disciples: "Disciples", people: "Everyday People" };

function ItemCard({ item, to, noun, i, blurb }) {
  return (
    <Reveal delay={Math.min(i, 8) * 0.04}>
      <Link
        to={to}
        data-testid={`facet-item-${item.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className="group flex h-full flex-col justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-gold"
      >
        <div>
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="font-serif text-xl font-semibold text-foreground">{item.name}</h3>
            <span className="shrink-0 font-mono text-xs text-muted-foreground">{item.count}</span>
          </div>
          {blurb && <p className="mt-1 text-sm text-muted-foreground" data-testid="facet-item-blurb">{blurb}</p>}
          {item.asked != null && item.received != null && (
            <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-muted-foreground" data-testid="facet-item-roles">
              {item.asked ? `asked ${item.asked}` : ""}{item.asked && item.received ? " · " : ""}{item.received ? `was asked ${item.received}` : ""}
            </p>
          )}
          {item.sample && <p className="mt-2 line-clamp-2 font-serif text-sm italic text-muted-foreground">&ldquo;{item.sample}&rdquo;</p>}
        </div>
        <span className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {item.books.slice(0, 3).join(" · ")}{item.books.length > 3 ? ` +${item.books.length - 3}` : ""}
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:text-gold" />
        </span>
      </Link>
    </Reveal>
  );
}

export default function FacetIndex({ facet: facetProp }) {
  const params = useParams();
  const facet = facetProp || params.facet;
  const meta = FACET_META[facet];
  const [filter, setFilter] = useState("");
  const { data, isLoading } = useQuery({
    queryKey: ["browse", facet],
    queryFn: async () => (await api.get(`/browse/${facet}`)).data,
  });

  const items = useMemo(() => {
    const all = data?.items || [];
    const f = filter.trim().toLowerCase();
    return f ? all.filter((it) => it.name.toLowerCase().includes(f)) : all;
  }, [data, filter]);

  const grouped = useMemo(() => {
    if (facet !== "who-asked" && facet !== "people") return null;
    const by = {};
    items.forEach((it) => (by[it.group || "people"] ||= []).push(it));
    return GROUP_ORDER.filter((g) => by[g]?.length).map((g) => ({ key: g, label: GROUP_LABELS[g], items: by[g] }));
  }, [items, facet]);

  if (!meta) return null;

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid={`facet-index-${facet}`}>
      <Seo title={meta.title} description={meta.intro} />
      <header className="mb-8">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{meta.eyebrow}</span>
        <KineticText as="h1" text={meta.title} className="mt-2 block font-serif text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl" testId="facet-headline" />
        <Reveal delay={0.3}>
          <p className="mt-3 max-w-2xl text-muted-foreground">{meta.intro}</p>
        </Reveal>
      </header>

      {(data?.items?.length || 0) > 12 && (
        <div className="relative mb-8 max-w-md">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={`Find a name… (${data.items.length})`}
            data-testid="facet-filter-input"
            className="w-full rounded-full border border-border bg-card py-2.5 pl-11 pr-4 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
        </div>
      )}

      {isLoading ? (
        <div className="flex min-h-[40vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : grouped ? (
        <div className="space-y-14">
          {grouped.map((g) => (
            <section key={g.key} data-testid={`facet-group-${g.key}`}>
              <div className="mb-5 flex items-center gap-3">
                <h2 className="font-serif text-2xl font-semibold text-foreground sm:text-3xl">{g.label}</h2>
                <span className="h-px flex-1 bg-border" />
                <span className="font-mono text-xs text-muted-foreground">{g.items.length}</span>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {g.items.map((it, i) => <ItemCard key={it.name} item={it} to={meta.itemPath(it.name)} noun={meta.noun} i={i} blurb={meta.blurbs?.[it.name]} />)}
              </div>
            </section>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {items.map((it, i) => <ItemCard key={it.name} item={it} to={meta.itemPath(it.name)} noun={meta.noun} i={i} blurb={meta.blurbs?.[it.name]} />)}
        </div>
      )}
      {!isLoading && items.length === 0 && (
        <p className="py-16 text-center text-muted-foreground" data-testid="facet-empty">Nothing matches &ldquo;{filter}&rdquo;.</p>
      )}
    </div>
  );
}
